/**
 * Work order search (server-backed with client-side fallback).
 *
 * Extracted verbatim from workOrdersService.ts — functions are invoked with
 * `this` bound to the WorkOrdersService instance (WorkOrdersCtx).
 */
import { extractItems } from '../../utils/apiHelpers';
/**
 * Search work orders
 */
export async function searchWorkOrders(locationId, query, limit = 8, include_past = false, include_deleted = false) {
    const q = (query || '').trim();
    if (!q)
        return [];
    try {
        // When no location is provided, omit location_id so the backend scopes
        // the search by the caller's accessible accounts (multi-account search).
        const params = {
            query: q,
            limit,
            include_past: String(include_past),
        };
        if (locationId)
            params.location_id = locationId;
        if (include_deleted)
            params.include_deleted = 'true';
        const response = await this.client.get(`/work-orders/search`, params);
        if (!response.success || !response.data) {
            // Location-scoped fallback only applies when a location is selected.
            if (locationId) {
                const locationResult = await this.getLocationWorkOrders(locationId, {
                    search: q,
                    limit,
                    sort_by: 'updated_at',
                    sort_desc: true,
                });
                return locationResult.work_orders.slice(0, limit);
            }
            return [];
        }
        // The server already filtered results via /work-orders/search — do NOT
        // re-filter client-side (matchesSearch covers fewer fields than the
        // backend and would drop valid hits).
        const list = extractItems(response.data, ['work_orders']);
        const normalized = list.map((wo) => this.normalizeWorkOrder(wo));
        return normalized.slice(0, limit);
    }
    catch (error) {
        console.error('[WorkOrdersService] Search work orders error (fallback to client-side):', error);
        if (locationId)
            return this.clientSideSearchWorkOrders(locationId, query, limit);
        return [];
    }
}
export async function searchAllWorkOrders(query, limit = 8, include_past = false) {
    try {
        const response = await this.client.get(`/work-orders/search`, { query, limit, include_past: String(include_past) });
        if (!response.success || !response.data) {
            return [];
        }
        const list = extractItems(response.data, ['work_orders']);
        return list.map((wo) => this.normalizeWorkOrder(wo));
    }
    catch (error) {
        console.error('[WorkOrdersService] Search work orders error (fallback to client-side):', error);
        return [];
    }
}
export async function clientSideSearchWorkOrders(locationId, query, limit) {
    const result = await this.getLocationWorkOrders(locationId, { limit: 100, sort_by: 'updated_at', sort_desc: true });
    return result.work_orders.filter((wo) => this.matchesSearch(wo, query)).slice(0, limit);
}
export function matchesSearch(workOrder, query) {
    const q = query.toLowerCase();
    if (workOrder.work_order_display_id?.toLowerCase().includes(q))
        return true;
    if (workOrder.name?.toLowerCase().includes(q))
        return true;
    if (workOrder.vehicle) {
        const vehicle = workOrder.vehicle;
        if (vehicle.vin?.toLowerCase().includes(q))
            return true;
        if (vehicle.make?.toLowerCase().includes(q))
            return true;
        if (vehicle.model?.toLowerCase().includes(q))
            return true;
        if (vehicle.year?.toString().includes(q))
            return true;
        if (vehicle.stock_number?.toLowerCase().includes(q))
            return true;
        const vehicleStr = [vehicle.year, vehicle.make, vehicle.model].filter(Boolean).join(' ').toLowerCase();
        if (vehicleStr.includes(q))
            return true;
    }
    if (workOrder.current_department?.name?.toLowerCase().includes(q))
        return true;
    if (workOrder.workflow_name?.toLowerCase().includes(q))
        return true;
    return false;
}
/**
 * Get recent work orders by VIN with detailed service information
 * Returns work orders sorted by status priority: in_progress first, then closed/completed
 * Includes detailed services for in-progress and recent completed work orders
 *
 * @param locationId - Location ID to search in
 * @param vin - Vehicle VIN to search for
 * @param limit - Maximum number of work orders to return
 * @returns Array of work orders with additional details about services
 */
export async function getRecentWorkOrdersByVin(locationId, vin, limit = 10, opts) {
    try {
        const workOrders = await this.searchWorkOrders(locationId, vin, limit, true, !!opts?.includeDeleted);
        const inProgressWorkOrders = workOrders.filter((wo) => wo.status === 'in_progress' || wo.status === 'open');
        const hasInProgressWorkOrder = inProgressWorkOrders.length > 0;
        const sortedWorkOrders = [...workOrders].sort((a, b) => {
            const aIsActive = a.status === 'in_progress' || a.status === 'open';
            const bIsActive = b.status === 'in_progress' || b.status === 'open';
            const aIsDeleted = !!(a.is_deleted || a.deleted_at);
            const bIsDeleted = !!(b.is_deleted || b.deleted_at);
            // Sort order: active → deleted → completed
            if (aIsActive && !bIsActive)
                return -1;
            if (!aIsActive && bIsActive)
                return 1;
            if (aIsDeleted && !bIsDeleted)
                return aIsActive ? 0 : -1;
            if (!aIsDeleted && bIsDeleted)
                return bIsActive ? 0 : 1;
            return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();
        });
        if (!opts?.enrichDetails) {
            // Return results without individual getWorkOrder enrichment to avoid N+1 API calls.
            // The overlay handles missing service details gracefully ("Click to view service details").
            return sortedWorkOrders.map((wo) => {
                const isActive = wo.status === 'in_progress' || wo.status === 'open';
                return {
                    ...wo,
                    hasInProgressWorkOrder,
                    canCreateNew: !isActive,
                };
            });
        }
        // Web behavior: enrich active + the 3 most recent with full details.
        return Promise.all(sortedWorkOrders.map(async (wo, index) => {
            try {
                const isActive = wo.status === 'in_progress' || wo.status === 'open';
                const isDeleted = !!(wo.is_deleted || wo.deleted_at);
                // Skip detail enrichment for deleted WOs (service data not relevant)
                if (isDeleted) {
                    return { ...wo, hasInProgressWorkOrder, canCreateNew: true };
                }
                if (isActive || index < 3) {
                    const detail = await this.getWorkOrder(wo.id);
                    return {
                        ...wo,
                        hasInProgressWorkOrder,
                        detailedServices: detail.sections,
                        canCreateNew: !isActive,
                    };
                }
                return { ...wo, hasInProgressWorkOrder, canCreateNew: true };
            }
            catch (error) {
                console.error(`[WorkOrdersService] Failed to fetch details for work order ${wo.id}:`, error);
                return { ...wo, hasInProgressWorkOrder, canCreateNew: !hasInProgressWorkOrder };
            }
        }));
    }
    catch (error) {
        console.error('[WorkOrdersService] Get work orders by VIN error:', error);
        throw error;
    }
}
